/*
 * Copyright (c) 2021 - 2026 All rights reserved by Ferenc Farkas <wolferi93@gmail.com>
 */

/**
 * Calendar API helper for the services-app (Management mode).
 * Reuses apiFetch from WorkSheetContext (Bearer auth, base already includes /api)
 * and getManagementScope for restaurant scoping. Mirrors the admin unified
 * calendar endpoints. Restaurant scope is sent both as the x-restaurant-id header
 * and a restaurantId query param so the backend resolveActiveRestaurantScopeId
 * always has it.
 */

import { Linking } from 'react-native';
import { apiFetch } from './WorkSheetContext';
import { getManagementScope } from './managementApi';
import { getUserInfo } from './storage';
import deviceCalendarSync from './deviceCalendarSync';

/**
 * apiFetch collapses a failed response into `Error('API <status>: <raw body>')`,
 * which throws away the meeting endpoints' machine-readable contract
 * ({ code, provider, needsConnect, authUrl }). Recover it so callers can react to
 * "connect your account first" instead of silently swallowing the failure.
 * @param {Error} error - Error thrown by apiFetch
 * @returns {Error} the same error, enriched with status/code/needsConnect/authUrl
 */
const enrichApiError = (error) => {
    const match = /^API (\d+): ([\s\S]*)$/.exec(String(error?.message || ''));

    if (!match) {
        return error;
    }
    error.status = Number(match[1]);
    try {
        const body = JSON.parse(match[2]);

        error.code = body.code || null;
        error.provider = body.provider || null;
        error.needsConnect = Boolean(body.needsConnect);
        error.authUrl = body.authUrl || null;
        if (body.message) {
            error.serverMessage = body.message;
        }
    } catch (_parseError) {
        /* Non-JSON body (proxy/HTML error page) — status alone is all we get. */
    }

    return error;
};

/**
 * Meeting platform -> the external account whose OAuth flow links it. Platforms
 * absent from this map need no account at all (Whereby, Jitsi).
 * @type {Object<string, string>}
 */
const MEETING_PLATFORM_ACCOUNT = {
    google_meet: 'google',
    teams: 'microsoft',
    zoom: 'zoom',
    webex: 'webex'
};

const buildQuery = (params = {}) => {
    const query = Object.entries(params)
        .filter(([, value]) => value !== undefined && value !== null && value !== '')
        .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`)
        .join('&');

    return query ? `?${query}` : '';
};

const scopeHeaders = (restaurantId) => (restaurantId ? { 'x-restaurant-id': restaurantId } : {});

export const getCurrentUserId = async () => {
    const info = await getUserInfo();

    return info?._id || info?.id || null;
};

export const combineDateTime = (dateStr, timeStr) => {
    // DateTimeModal yields split local strings 'YYYY-MM-DD' + 'HH:mm'. Build a
    // local Date and serialize to ISO for the wire.
    if (!dateStr) {
        return null;
    }
    const [y, mo, d] = String(dateStr).split('-').map(Number);
    const [h, mi] = String(timeStr || '00:00').split(':').map(Number);
    const date = new Date(y, (mo || 1) - 1, d || 1, h || 0, mi || 0, 0);

    return date.toISOString();
};

const unwrapDocs = (response) => {
    if (Array.isArray(response)) {
        return response;
    }
    if (Array.isArray(response?.docs)) {
        return response.docs;
    }
    if (Array.isArray(response?.data)) {
        return response.data;
    }

    return [];
};

/**
 * Fire-and-forget side effects after a successful event mutation: queue a
 * debounced device-calendar mirror pass (deviceCalendarSync guards its own
 * enabled flag + permission, so this is a no-op for users who never turned the
 * mirror on) and refresh the home-screen widgets so their calendar section
 * reflects the change. Never throws and never delays the API response.
 * The mirror receives a LOADER (not events) so the debounced pass that
 * eventually runs fetches a fresh upcoming window — including the
 * `notifications` reminder fields the mirror maps to native alarms.
 * @returns {void}
 */
const notifyCalendarMutation = () => {
    try {
        deviceCalendarSync.scheduleSync(() => {
            const start = new Date();
            const end = new Date(start.getTime() + 60 * 24 * 60 * 60 * 1000);

            return calendarApi.fetchMyEvents({ start: start.toISOString(), end: end.toISOString() });
        });
    } catch (_error) {
        /* Mirror is best-effort — never let it break the mutation flow. */
    }
    try {
        // Lazily required (mirroring widgetRefresh's own internal pattern):
        // widgetRefresh transitively pulls native widget modules that must not
        // load eagerly on every calendarApi consumer, and requiring here also
        // avoids the calendarApi → widgetRefresh → buildWidgetSnapshot →
        // calendarApi import cycle.
        // eslint-disable-next-line global-require
        const { refreshWidgets } = require('./widgets/widgetRefresh');

        refreshWidgets().catch(() => {});
    } catch (_error) {
        /* Widget refresh is best-effort too. */
    }
};

const calendarApi = {
    /** Personal (My-Calendar) events for the current user. */
    fetchMyEvents: async ({ start, end }) => {
        const scope = await getManagementScope();
        const userId = await getCurrentUserId();
        const query = buildQuery({
            type: 'personal',
            userId,
            start,
            end,
            restaurantId: scope.restaurantId
        });
        const response = await apiFetch(`/calendar/events${query}`, { headers: scopeHeaders(scope.restaurantId) });

        return unwrapDocs(response);
    },

    /** Restaurant-scoped expert directory (Shared mode selector). */
    fetchExperts: async () => {
        const scope = await getManagementScope();
        const query = buildQuery({ restaurantId: scope.restaurantId });
        const response = await apiFetch(`/calendar/experts${query}`, { headers: scopeHeaders(scope.restaurantId) });

        return unwrapDocs(response);
    },

    /** Shared-mode per-expert payloads (events + busy/free + availability). */
    fetchShared: async ({ expertIds, start, end }) => {
        if (!Array.isArray(expertIds) || expertIds.length === 0) {
            return [];
        }
        const scope = await getManagementScope();
        const query = buildQuery({
            expertIds: expertIds.join(','),
            start,
            end,
            restaurantId: scope.restaurantId
        });
        const response = await apiFetch(`/calendar/shared${query}`, { headers: scopeHeaders(scope.restaurantId) });

        return Array.isArray(response?.experts) ? response.experts : [];
    },

    /** Conflict-guard check for a proposed expert booking. */
    checkConflict: async ({ expertId, start, end, excludeEventId }) => {
        const scope = await getManagementScope();
        const query = buildQuery({
            expertId,
            start,
            end,
            excludeEventId,
            restaurantId: scope.restaurantId
        });

        return apiFetch(`/calendar/conflicts${query}`, { headers: scopeHeaders(scope.restaurantId) });
    },

    /** Read-only domain feed layers. */
    fetchFeed: async ({ start, end, sources }) => {
        if (!Array.isArray(sources) || sources.length === 0) {
            return [];
        }
        const scope = await getManagementScope();
        const query = buildQuery({
            start,
            end,
            sources: sources.join(','),
            restaurantId: scope.restaurantId
        });
        const response = await apiFetch(`/calendar/feed${query}`, { headers: scopeHeaders(scope.restaurantId) });

        return unwrapDocs(response);
    },

    createEvent: async (payload) => {
        const scope = await getManagementScope();
        const response = await apiFetch('/calendar/events', {
            method: 'POST',
            headers: scopeHeaders(scope.restaurantId),
            body: JSON.stringify({
                ...payload,
                restaurantId: scope.restaurantId
            })
        });

        notifyCalendarMutation();

        return response;
    },

    updateEvent: async (id, payload) => {
        const scope = await getManagementScope();
        const response = await apiFetch(`/calendar/events/${id}`, {
            method: 'PUT',
            headers: scopeHeaders(scope.restaurantId),
            body: JSON.stringify(payload)
        });

        notifyCalendarMutation();

        return response;
    },

    deleteEvent: async (id) => {
        const scope = await getManagementScope();
        const response = await apiFetch(`/calendar/events/${id}`, {
            method: 'DELETE',
            headers: scopeHeaders(scope.restaurantId)
        });

        notifyCalendarMutation();

        return response;
    },

    /** Per-user availability (weekly + unavailability) for the Settings screen. */
    fetchAvailability: async () => {
        const scope = await getManagementScope();
        const query = buildQuery({ restaurantId: scope.restaurantId });

        return apiFetch(`/calendar/availability/me${query}`, { headers: scopeHeaders(scope.restaurantId) });
    },

    saveAvailability: async (payload) => {
        const scope = await getManagementScope();
        const query = buildQuery({ restaurantId: scope.restaurantId });

        return apiFetch(`/calendar/availability/me${query}`, {
            method: 'PUT',
            headers: scopeHeaders(scope.restaurantId),
            body: JSON.stringify(payload)
        });
    },

    /**
     * "Foglalt vagyok" — append a one-off unavailability block to the current
     * user's availability, mirroring the admin markMyselfBusy shortcut. Merges
     * onto the existing rules so nothing the user already configured is lost.
     */
    markMyselfBusy: async ({ start, end, reason }) => {
        const current = await calendarApi.fetchAvailability().catch(() => null);
        const unavailability = Array.isArray(current?.unavailability)
            ? current.unavailability.slice()
            : [];

        unavailability.push({
            type: 'date-range',
            start,
            end,
            reason: reason || '',
            allDay: false
        });

        return calendarApi.saveAvailability({ unavailability });
    },

    /** Calendar connection status for all providers (Google, Apple, Microsoft). */
    fetchConnectionStatus: async () => {
        const userId = await getCurrentUserId();
        const safe = (provider) => apiFetch(`/calendar/${provider}/status?userId=${userId}`).catch(() => ({ connected: false }));
        const [google, apple, microsoft] = await Promise.all([safe('google'), safe('apple'), safe('microsoft')]);

        return {
            google: google || { connected: false },
            apple: apple || { connected: false },
            microsoft: microsoft || { connected: false }
        };
    },

    getGoogleAuthUrl: async () => {
        const userId = await getCurrentUserId();
        const response = await apiFetch(`/calendar/google/auth?userId=${userId}`);

        return response?.authUrl || null;
    },

    /** Microsoft (Outlook) OAuth consent URL — mirrors getGoogleAuthUrl. */
    getMicrosoftAuthUrl: async () => {
        const userId = await getCurrentUserId();
        const response = await apiFetch(`/calendar/microsoft/auth?userId=${userId}`);

        return response?.authUrl || null;
    },

    /**
     * Mint a real online-meeting link from the user's connected calendar account
     * and return its URL. `provider` is the CalendarEvent.meetingProvider value:
     * 'teams' goes to Microsoft Graph, anything else to Google Meet. Both reuse
     * the calendar consent already granted — no extra permission prompt.
     * @param {string} provider - 'google_meet' | 'teams'
     * @param {Object} [context] - { title, description, start, end } ISO strings
     * @returns {Promise<?string>} the join URL, or null when the API returns none
     */
    generateMeetingLink: async (provider, context = {}) => {
        const userId = await getCurrentUserId();

        try {
            const response = await apiFetch('/calendar/meeting-link', {
                method: 'POST',
                body: JSON.stringify({
                    provider,
                    userId,
                    title: context.title,
                    description: context.description,
                    start: context.start,
                    end: context.end
                })
            });

            return response?.meetLink || null;
        } catch (error) {
            // Rethrow with needsConnect/authUrl intact so the caller can run the
            // provider's consent flow and retry, instead of dead-ending.
            throw enrichApiError(error);
        }
    },

    /**
     * Which meeting platforms this deployment can generate links for, and which
     * accounts this user has already linked. Lets the UI offer only buttons that
     * can actually succeed.
     * @returns {Promise<Array<Object>>} capability entries (empty on failure)
     */
    fetchMeetingProviders: async () => {
        const userId = await getCurrentUserId();
        const response = await apiFetch(`/calendar/meeting-providers?userId=${userId}`)
            .catch(() => null);

        return Array.isArray(response?.providers) ? response.providers : [];
    },

    /**
     * Link the user's own Google / Microsoft account so meetings can be hosted by
     * it: open the provider's consent page in the system browser (reusing an
     * `authUrl` a failed generation already returned, when present) and poll the
     * connection status until it flips to connected. Mirrors the connect flow in
     * CalendarSettingsScreen so both places behave identically.
     *
     * @param {string} provider - a meeting platform ('google_meet', 'teams',
     *   'zoom', 'webex') or an account provider ('google', 'microsoft', ...)
     * @param {string} [authUrl] - Pre-minted consent URL; fetched when omitted.
     * @param {Object} [opts] - { attempts, intervalMs } polling budget
     * @returns {Promise<boolean>} true once the account reports as connected
     */
    connectProviderAccount: async (provider, authUrl, opts = {}) => {
        const account = MEETING_PLATFORM_ACCOUNT[provider] || provider;
        let url = authUrl;

        if (!url) {
            if (account === 'google') {
                url = await calendarApi.getGoogleAuthUrl();
            } else if (account === 'microsoft') {
                url = await calendarApi.getMicrosoftAuthUrl();
            } else {
                // Zoom / Webex and any future conferencing-only account.
                const response = await apiFetch(
                    `/calendar/conferencing/${account}/auth`
                ).catch(() => null);

                url = response?.authUrl || null;
            }
        }

        if (!url) {
            return false;
        }
        await Linking.openURL(url);

        const attempts = opts.attempts || 45;
        const intervalMs = opts.intervalMs || 4000;

        for (let attempt = 0; attempt < attempts; attempt += 1) {
            // eslint-disable-next-line no-await-in-loop
            await new Promise((resolve) => { globalThis.setTimeout(resolve, intervalMs); });
            // eslint-disable-next-line no-await-in-loop
            const connected = await calendarApi.isAccountConnected(account).catch(() => false);

            if (connected) {
                return true;
            }
        }

        return false;
    },

    /**
     * Whether one external account is currently linked. Calendar providers come
     * from the calendar status endpoint; conferencing-only accounts (Zoom,
     * Webex) from their own status route.
     * @param {string} account - 'google' | 'microsoft' | 'apple' | 'zoom' | 'webex'
     * @returns {Promise<boolean>}
     */
    isAccountConnected: async (account) => {
        if (account === 'zoom' || account === 'webex') {
            const response = await apiFetch(`/calendar/conferencing/${account}/status`)
                .catch(() => null);

            return Boolean(response?.connected);
        }

        const status = await calendarApi.fetchConnectionStatus().catch(() => null);

        return Boolean(status?.[account]?.connected);
    },

    connectApple: async ({ username, password, calendarUrl }) => {
        const userId = await getCurrentUserId();

        return apiFetch('/calendar/apple/connect', {
            method: 'POST',
            body: JSON.stringify({
                userId,
                username,
                password,
                calendarUrl
            })
        });
    },

    syncProvider: async (provider, direction = 'bidirectional') => {
        const userId = await getCurrentUserId();

        return apiFetch(`/calendar/${provider}/sync`, {
            method: 'POST',
            body: JSON.stringify({
                userId,
                direction
            })
        });
    },

    disconnectProvider: async (provider) => {
        const userId = await getCurrentUserId();

        return apiFetch(`/calendar/${provider}/disconnect`, {
            method: 'DELETE',
            body: JSON.stringify({ userId })
        });
    }
};

export default calendarApi;
